/**
 * SipContext.jsx — Motor de telefonía WebRTC para el sistema de cobranzas
 *
 * CÓMO FUNCIONA EN RESUMEN:
 *   1. El agente conecta su extensión → se llama connect() → JsSIP se registra en Asterisk vía WebSocket seguro (WSS).
 *   2. El agente marca un número → se llama makeCall() → JsSIP envía un INVITE SIP a Asterisk.
 *   3. Asterisk negocia ICE (ruta de red), DTLS (encriptación) y SRTP (audio encriptado).
 *   4. El audio llega al elemento <audio> que vive dentro de este Provider.
 *
 * POR QUÉ USAR CONTEXT (no props):
 *   El teléfono necesita estar activo mientras el agente navega entre módulos.
 *   El SipProvider se pone UNA SOLA VEZ en la raíz del app (App.js) y nunca
 *   se desmonta, así que la conexión SIP sobrevive a cualquier cambio de ruta.
 *   Cualquier componente hijo puede acceder al teléfono con useSip().
 */

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import JsSIP from 'jssip';
import axios from '../configApi/axiosConfig';
import { APIURL } from '../configApi/apiConfig';

// ─── IP del servidor Issabel ────────────────────────────────────────────────
// Se toma de la variable de entorno REACT_APP_SIP_DOMAIN (archivo .env del proyecto).
// El puerto (8089) y la ruta (/ws) son fijos según la configuración de Issabel.
const SIP_DOMAIN = process.env.REACT_APP_SIP_DOMAIN;

const SIP_CONFIG = {
    domain: SIP_DOMAIN,
    websocketUrl: `wss://${SIP_DOMAIN}:8089/ws`,
};
// ────────────────────────────────────────────────────────────────────────────

const SipContext = createContext(null);

// En sessionStorage va solo { username, extension }: la clave SIP queda en memoria
// y, al recargar o abrir otra pestaña, se vuelve a pedir al backend con el token.
const SIP_SESSION_KEY = 'sip_creds';

async function recuperarCredenciales(guardadas) {
    if (guardadas.password) return guardadas; // formato anterior (con clave): se usa una vez y se reescribe sin ella
    if (!localStorage.getItem('token')) return null;
    const { data } = await axios.get(APIURL.get_mi_extension());
    const ext = Array.isArray(data) ? data[0] : data;
    if (!ext?.extension || !ext?.password_sip) return null;
    return { username: ext.extension, extension: ext.extension, password: ext.password_sip };
}

// ─── Una sola pestaña con el teléfono ───────────────────────────────────────
// Abrir un cliente en pestaña nueva (window.open) copia el sessionStorage, y
// cada pestaña registraba LA MISMA extensión: varios softphones peleándose el
// registro en Asterisk, reintentos cruzados y, al final, la PC bloqueada por
// fail2ban. Ahora las pestañas se coordinan por BroadcastChannel: la que
// conecta "reclama" el teléfono y la que lo tenía se des-registra y queda en
// espera ("Usar aquí"). Si la que lo tiene está en llamada, no lo cede.
const SIP_CHANNEL_NAME = 'point_telefono';
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const ESPERA_RESPUESTA_MS = 300;   // nadie responde → no hay otra pestaña con teléfono
const ESPERA_LIBERACION_MS = 2000; // lo que se espera a que la otra pestaña se des-registre

// ─── Reintentos acotados ────────────────────────────────────────────────────
// Sin límite, una PC que no puede conectar reintentaba cada pocos segundos para
// siempre. Tras MAX_FALLOS seguidos se detiene y pide avisar a sistemas.
const MAX_FALLOS = 6;
// OPTIONS periódico: mantiene viva la conexión WSS para que la red no la cierre
// por inactividad (las reconexiones cada ~3 minutos que se ven en Asterisk).
const KEEPALIVE_MS = 25000;

const MSG_ERROR_WSS = `No se pudo abrir la conexión segura con la central. Puede ser el certificado `
    + `(abra https://${SIP_DOMAIN}:8089/ws en el navegador y acéptelo) o que esta PC esté bloqueada. Avise a sistemas.`;
const MSG_ERROR_RED = 'Se perdió la conexión con la central y no se pudo recuperar. Pulse "Reintentar" o avise a sistemas.';
const MSG_ERROR_CLAVE = 'La central rechazó la clave de la extensión. Vuelva a iniciar sesión o avise a sistemas.';

// ─── Trazabilidad de llamadas ───────────────────────────────────────────────
// Toda llamada que sale de POINT debe llevar el idCompra en la cabecera
// X-Tracking-ID. Asterisk lo guarda en el CDR (campo userfield) y así cada
// llamada queda enlazada con su gestión, su grabación y su transcripción.
// Sin idCompra no se permite marcar.
export function esIdCompraValido(idCompra) {
    return /^\d+$/.test(String(idCompra ?? '').trim()) && Number(idCompra) > 0;
}

// Normaliza el destino al plan de marcado de Issabel (salida con prefijo 9):
//   celular 09XXXXXXXX → 909XXXXXXXX · fijo 0[2-7]XXXXXXX → 90[2-7]XXXXXXX
//   +593 / 593 se convierten a 0 · extensiones internas quedan igual
export function normalizarDestino(numero) {
    let n = String(numero ?? '').trim().replace(/[^\d+]/g, '');
    if (n.startsWith('+593')) n = `0${n.slice(4)}`;
    else if (n.startsWith('593') && n.length === 12) n = `0${n.slice(3)}`;
    n = n.replace(/\D/g, '');
    if (/^0\d{9}$/.test(n) || /^0[2-7]\d{7}$/.test(n)) return `9${n}`;
    return n;
}

// ── Íconos SVG inline ────────────────────────────────────────────────────────
const IconError = () => (
    <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
);
const IconWarning = () => (
    <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374l7.228-12.623c.866-1.5 3.032-1.5 3.898 0l7.228 12.623z" />
    </svg>
);
const IconClose = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const TOAST_STYLES = {
    error: 'bg-red-600 text-white',
    warning: 'bg-yellow-500 text-white',
    info: 'bg-blue-600 text-white',
};

function Toast({ id, type, message, onDismiss }) {
    return (
        <div className={`flex items-start gap-3 px-4 py-3 rounded-lg shadow-xl text-sm max-w-sm w-full pointer-events-auto ${TOAST_STYLES[type] ?? TOAST_STYLES.info}`}>
            {type === 'error' ? <IconError /> : <IconWarning />}
            <span className="flex-1 leading-snug">{message}</span>
            <button
                onClick={() => onDismiss(id)}
                className="opacity-70 hover:opacity-100 transition-opacity mt-0.5"
                aria-label="Cerrar notificación"
            >
                <IconClose />
            </button>
        </div>
    );
}

export function SipProvider({ children }) {

    // ── useRef vs useState ────────────────────────────────────────────────
    // useRef: para objetos que deben persistir entre renders SIN causar un
    //         nuevo render cuando cambian. El UA de JsSIP y la sesión activa
    //         NO deben recrearse en cada render — si estuvieran en useState,
    //         React los destruiría y recrearía cortando la llamada.
    // useState: para valores que SÍ deben actualizar la UI (el punto de color,
    //           el texto de estado, el cronómetro).
    // ─────────────────────────────────────────────────────────────────────

    const uaRef = useRef(null); // JsSIP User Agent (la "central" del lado del navegador)
    const sessionRef = useRef(null); // Llamada activa en curso
    const audioRef = useRef(null); // Elemento <audio> donde suena la voz remota
    const timerRef = useRef(null); // ID del setInterval del cronómetro
    // Evita mostrar "Llamada fallida: Canceled" cuando ya se mostró un error más específico
    const suppressNextFailedToastRef = useRef(false);
    // true desde que se pulsa "llamar" hasta que la llamada termina. Es un ref (no
    // estado) para que un doble clic no alcance a crear una segunda llamada
    // mientras se espera el permiso del micrófono.
    const marcandoRef = useRef(false);
    const credsRef = useRef(null);      // credenciales de esta pestaña (para "Usar aquí" / "Reintentar")
    const canalRef = useRef(null);      // BroadcastChannel entre pestañas
    const reclamoRef = useRef(null);    // timestamp del reclamo en curso (null si no hay)
    const generacionRef = useRef(0);    // invalida un connect() que quedó esperando si llega otro o un disconnect()
    const fallosRef = useRef(0);        // fallos seguidos de conexión/registro
    const keepaliveRef = useRef(null);  // setInterval del OPTIONS
    const reintentoRef = useRef(null);  // setTimeout del re-registro

    // Estados que actualizan la UI
    const [status, setStatus] = useState('disconnected'); // estado del punto de color
    const [statusText, setStatusText] = useState('Desconectado'); // texto descriptivo del estado
    const [extension, setExtension] = useState(null);           // extensión registrada
    const [phoneAvailable, setPhoneAvailable] = useState(false);
    const [callState, setCallState] = useState('idle');         // 'idle' | 'ringing' | 'active'
    const [callSeconds, setCallSeconds] = useState(0);              // segundos de la llamada activa
    const [callTarget, setCallTarget] = useState(null);
    const [callClientName, setCallClientName] = useState(null);
    const [trackingId, setTrackingId] = useState(null);
    const [dialerRequest, setDialerRequest] = useState(null);
    const [notifications, setNotifications] = useState([]);
    // Cliente abierto en pantalla ({ idCompra, clientName }); lo publica la
    // página de detalle de cobranza para que el marcador llame con su idCompra.
    const [contextoGestion, setContextoGestion] = useState(null);

    // Canal entre pestañas + auto-reconexión al hacer F5 o al abrir una pestaña nueva:
    // si existen credenciales en sessionStorage, reconectar (reclamando el teléfono).
    // Solo al montar: el Provider vive toda la sesión.
    useEffect(() => {
        if (typeof BroadcastChannel !== 'undefined') {
            const canal = new BroadcastChannel(SIP_CHANNEL_NAME);
            canal.onmessage = ({ data }) => atenderMensajeCanal(data);
            canalRef.current = canal;
        }

        const raw = sessionStorage.getItem(SIP_SESSION_KEY);
        if (raw) {
            const generacion = generacionRef.current;
            Promise.resolve()
                .then(() => recuperarCredenciales(JSON.parse(raw)))
                .then((creds) => {
                    if (generacion !== generacionRef.current) return; // ya hubo un login/logout
                    if (creds) connect(creds);
                    else sessionStorage.removeItem(SIP_SESSION_KEY);
                })
                .catch(() => sessionStorage.removeItem(SIP_SESSION_KEY));
        }

        return () => {
            canalRef.current?.close();
            canalRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Al cerrar la pestaña o recargar: des-registrar. Si no, Asterisk sigue
    // escribiendo a un WebSocket muerto ("Serious Network Trouble") hasta que
    // vence el registro.
    useEffect(() => {
        const alSalir = () => {
            const ua = uaRef.current;
            uaRef.current = null;
            ua?.stop();
        };
        // Pestaña restaurada desde la caché de atrás/adelante: pagehide ya la des-registró.
        const alVolver = (e) => {
            if (e.persisted && credsRef.current) connect(credsRef.current);
        };
        window.addEventListener('pagehide', alSalir);
        window.addEventListener('pageshow', alVolver);
        return () => {
            window.removeEventListener('pagehide', alSalir);
            window.removeEventListener('pageshow', alVolver);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Helpers internos ─────────────────────────────────────────────────

    function showToast(type, message) {
        const id = Date.now() + Math.random();
        setNotifications(prev => [...prev, { id, type, message }]);
        const duration = type === 'error' ? 7000 : 5000;
        setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), duration);
    }

    function dismissToast(id) {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }

    function updateStatus(state, text) {
        setStatus(state);
        setStatusText(text);
    }

    function startCallTimer() {
        setCallSeconds(0);
        timerRef.current = setInterval(() => setCallSeconds(s => s + 1), 1000);
    }

    function stopCallTimer() {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCallSeconds(0);
    }

    // Se llama al terminar o fallar una llamada: limpia todo el estado de llamada.
    function endCallCleanUp() {
        suppressNextFailedToastRef.current = false;
        marcandoRef.current = false;
        sessionRef.current = null;
        setCallState('idle');
        setCallTarget(null);
        setCallClientName(null);
        setTrackingId(null);
        stopCallTimer();
        updateStatus('connected', 'Listo para marcar');
    }

    function resetSipState() {
        marcandoRef.current = false;
        sessionRef.current = null;
        setCallState('idle');
        setCallTarget(null);
        setCallClientName(null);
        setTrackingId(null);
        stopCallTimer();
        setExtension(null);
        setPhoneAvailable(false);
        updateStatus('disconnected', 'Desconectado');
    }

    function detenerKeepalive() {
        clearInterval(keepaliveRef.current);
        keepaliveRef.current = null;
    }

    function iniciarKeepalive(ua) {
        detenerKeepalive();
        keepaliveRef.current = setInterval(() => {
            if (uaRef.current !== ua || !ua.isRegistered()) return;
            try {
                ua.sendOptions(`sip:${SIP_CONFIG.domain}`);
            } catch { }
        }, KEEPALIVE_MS);
    }

    // Detiene el UA actual (des-registra) y sus timers. Se suelta la referencia
    // ANTES de stop() para que sus eventos ya no toquen el estado.
    function detenerUA() {
        detenerKeepalive();
        clearTimeout(reintentoRef.current);
        reintentoRef.current = null;
        const ua = uaRef.current;
        uaRef.current = null;
        ua?.stop();
    }

    // Esta pestaña queda sin teléfono, pero con el panel visible para "Usar aquí".
    function pasarAEspera(texto = 'Teléfono activo en otra pestaña') {
        setExtension(null);
        setPhoneAvailable(true);
        updateStatus('standby', texto);
    }

    // Cuenta un fallo seguido. Al llegar a MAX_FALLOS deja de reintentar: así una
    // PC que no puede conectar no martilla a Asterisk hasta que fail2ban la bloquee.
    // Devuelve true si se detuvo.
    function registrarFallo(mensajeFinal) {
        fallosRef.current += 1;
        if (fallosRef.current >= MAX_FALLOS) {
            detenerUA();
            setExtension(null);
            updateStatus('error', mensajeFinal);
            showToast('error', mensajeFinal);
            return true;
        }
        updateStatus('connecting', `Reconectando... (intento ${fallosRef.current} de ${MAX_FALLOS - 1})`);
        return false;
    }

    // ── Coordinación entre pestañas ──────────────────────────────────────
    //
    //   claim    {from, ts}  "voy a conectar el teléfono"
    //   yielding {from, to}  "lo tengo, lo estoy soltando"   → esperar 'released'
    //   released {from, to}  "ya me des-registré"            → conectar
    //   busy     {from, to}  "lo tengo y estoy en llamada"   → quedar en espera
    //
    // Si nadie responde en ESPERA_RESPUESTA_MS, no hay otra pestaña con teléfono.

    function atenderMensajeCanal(data) {
        if (!data || data.from === TAB_ID || data.type !== 'claim') return;
        const canal = canalRef.current;

        // Dos pestañas reclamando a la vez (p. ej. abiertas juntas): gana el reclamo más nuevo.
        if (reclamoRef.current && !uaRef.current) {
            const esMasNuevo = data.ts > reclamoRef.current
                || (data.ts === reclamoRef.current && data.from > TAB_ID);
            if (esMasNuevo) {
                generacionRef.current++;
                reclamoRef.current = null;
                pasarAEspera();
            }
            return;
        }

        if (!uaRef.current) return;

        if (sessionRef.current || marcandoRef.current) {
            canal?.postMessage({ type: 'busy', from: TAB_ID, to: data.from });
            return;
        }

        canal?.postMessage({ type: 'yielding', from: TAB_ID, to: data.from });
        cederTelefono(data.from);
    }

    // Suelta el teléfono y avisa cuando Asterisk ya confirmó el des-registro: si
    // la otra pestaña registrara antes, este un-REGISTER le borraría el contacto.
    function cederTelefono(destino) {
        const ua = uaRef.current;
        let avisado = false;
        const avisar = () => {
            if (avisado) return;
            avisado = true;
            canalRef.current?.postMessage({ type: 'released', from: TAB_ID, to: destino });
        };
        if (ua?.isRegistered()) {
            ua.once('unregistered', avisar);
            setTimeout(avisar, ESPERA_LIBERACION_MS - 500);
        } else {
            avisar();
        }
        detenerUA();
        pasarAEspera();
    }

    // Resuelve true si esta pestaña puede registrar, false si otra está en llamada.
    function reclamarTelefono() {
        const canal = canalRef.current;
        if (!canal) return Promise.resolve(true);

        const ts = Date.now();
        reclamoRef.current = ts;

        return new Promise((resolve) => {
            let espera = setTimeout(() => terminar(true), ESPERA_RESPUESTA_MS);

            function terminar(libre) {
                clearTimeout(espera);
                canal.removeEventListener('message', alResponder);
                resolve(libre);
            }

            function alResponder({ data }) {
                if (data?.to !== TAB_ID) return;
                if (data.type === 'busy') terminar(false);
                else if (data.type === 'released') terminar(true);
                else if (data.type === 'yielding') {
                    clearTimeout(espera);
                    espera = setTimeout(() => terminar(true), ESPERA_LIBERACION_MS);
                }
            }

            canal.addEventListener('message', alResponder);
            canal.postMessage({ type: 'claim', from: TAB_ID, ts });
        });
    }

    // ── Listeners del RTCPeerConnection (capa WebRTC) ────────────────────
    //
    // WebRTC usa tres capas para establecer audio:
    //   ICE   → encuentra la ruta de red entre el navegador y Asterisk
    //   DTLS  → hace el handshake de encriptación (como TLS pero sobre UDP)
    //   SRTP  → envía el audio ya encriptado
    //
    // Esta función adjunta los eventos del RTCPeerConnection para:
    //   - recibir el audio remoto cuando llega el 'track'
    //   - detectar si ICE falla (red no alcanzable)
    //   - loguear el estado de la conexión DTLS

    function setupPeerConnectionListeners(pc) {
        // 'track' se dispara cuando Asterisk envía audio al navegador.
        // Asignamos el stream al elemento <audio> y forzamos reproducción
        // porque el autoplay del navegador puede estar bloqueado.
        pc.addEventListener('track', (e) => {
            const audio = audioRef.current;
            if (!audio) return;
            if (e.streams?.[0]) {
                audio.srcObject = e.streams[0];
            } else if (e.track) {
                // Fallback: algunos navegadores no llenan e.streams, se crea el stream manualmente
                const stream = audio.srcObject instanceof MediaStream
                    ? audio.srcObject
                    : new MediaStream();
                stream.addTrack(e.track);
                audio.srcObject = stream;
            }
            audio.play().catch(() => { });
        });

        // ICE 'failed' significa que el navegador no pudo encontrar
        // ninguna ruta de red hacia Asterisk (problema de firewall/red).
        pc.addEventListener('iceconnectionstatechange', () => {
            if (pc.iceConnectionState === 'failed') {
                showToast('error', 'Error de red (ICE failed). El servidor no es alcanzable para audio.');
                sessionRef.current?.terminate();
                endCallCleanUp();
            }
        });

        // connectionState refleja el estado completo de WebRTC (ICE + DTLS).
        // 'connected' = todo ok, audio fluyendo. 'failed' = error irrecuperable.
        pc.addEventListener('connectionstatechange', () => {
            //console.log('WebRTC conexión:', pc.connectionState);
        });
    }

    // ── Listeners de la sesión SIP ────────────────────────────────────────
    //
    // Cada llamada (entrante o saliente) es un objeto RTCSession de JsSIP.
    // Aquí adjuntamos todos los eventos del ciclo de vida de la llamada.

    function setupSessionListeners(session) {
        // NOTA IMPORTANTE — Bug de orden de eventos en JsSIP 3.x:
        // En llamadas SALIENTES, JsSIP dispara 'peerconnection' ANTES que
        // 'newRTCSession'. Para cuando nuestro código recibe la sesión,
        // el evento ya pasó y nunca lo capturamos.
        // SOLUCIÓN: en newRTCSession (ver más abajo), verificamos si
        // session.connection ya existe y adjuntamos los listeners directamente.
        // Este handler de 'peerconnection' aquí solo aplica a llamadas ENTRANTES,
        // donde el orden es el contrario (newRTCSession primero, peerconnection después).
        session.on('peerconnection', (data) => {
            setupPeerConnectionListeners(data.peerconnection);
        });

        // Si es llamada entrante: mostrar quién llama y responder automáticamente.
        if (session.direction === 'incoming') {
            const caller = session.remote_identity.uri.user;
            setCallTarget(caller);
            setCallClientName(null);
            updateStatus('connecting', `Llamada entrante de ${caller}`);
            setCallState('ringing');
            session.answer({ mediaConstraints: { audio: true, video: false } });
        }

        // No se pudo acceder al micrófono durante la llamada
        session.on('getusermediafailed', (err) => {
            showToast('error', 'No se pudo acceder al micrófono: ' + (err.message || err));
            endCallCleanUp();
        });

        // 'progress' = Asterisk está procesando (100 Trying) o sonando (180 Ringing)
        session.on('progress', (e) => {
            const code = e.response?.status_code ?? '';
            updateStatus('connecting', `Timbrado... ${code}`);
            setCallState('ringing');
        });

        // Error al aplicar el SDP de Asterisk. Casi siempre es el certificado
        // DTLS no configurado en Issabel (dtlscertfile vacío o archivo inexistente).
        // Marcamos el flag para que el evento 'failed' subsecuente (causa: Canceled)
        // no muestre un segundo toast redundante.
        session.on('peerconnection:setremotedescriptionfailed', () => {
            suppressNextFailedToastRef.current = true;
            showToast('error', 'Error DTLS en Asterisk. Verifica que el certificado dtls.pem esté generado y configurado en Issabel.');
            sessionRef.current?.terminate();
            endCallCleanUp();
        });

        // 'accepted' = el destinatario contestó (200 OK de SIP recibido)
        session.on('accepted', () => {
            updateStatus('connected', 'En llamada activa');
            setCallState('active');
            startCallTimer();
        });

        // 'failed' = la llamada no pudo conectarse por alguna razón
        // Los causes más comunes: Rejected (ocupado), No Answer, Not Found, Unavailable
        session.on('failed', (e) => {
            if (suppressNextFailedToastRef.current) {
                suppressNextFailedToastRef.current = false;
                endCallCleanUp();
                return;
            }
            const cause = e.cause || 'desconocida';
            const code = e.response ? ` (${e.response.status_code} ${e.response.reason_phrase})` : '';
            showToast('warning', `Llamada fallida: ${cause}${code}`);
            endCallCleanUp();
        });

        // 'ended' = llamada terminada normalmente (alguien colgó)
        session.on('ended', () => endCallCleanUp());
    }

    // ── API pública ──────────────────────────────────────────────────────

    /**
     * connect({ username, extension, password })
     * Registra la extensión SIP en Asterisk.
     *
     * Flujo interno:
     *   1. Crea un WebSocket seguro (WSS) hacia Asterisk puerto 8089
     *   2. JsSIP envía REGISTER → Asterisk responde 401 → JsSIP reenvía con hash MD5
     *   3. Asterisk responde 200 OK → el agente queda registrado
     *
     * Antes de registrar, reclama el teléfono a las otras pestañas (ver arriba).
     */
    async function connect(creds) {
        const generacion = ++generacionRef.current;
        detenerUA();
        resetSipState();
        setPhoneAvailable(true);
        updateStatus('connecting', 'Conectando...');

        credsRef.current = creds;
        sessionStorage.setItem(SIP_SESSION_KEY, JSON.stringify({ username: creds.username, extension: creds.extension }));

        const libre = await reclamarTelefono();
        if (generacion !== generacionRef.current) return; // llegó otro connect()/disconnect() mientras esperaba
        reclamoRef.current = null;
        if (!libre) {
            pasarAEspera('Teléfono en llamada en otra pestaña');
            return;
        }
        iniciarUA(creds);
    }

    function iniciarUA({ username, extension: ext, password }) {
        fallosRef.current = 0;

        // Logs de JsSIP solo en desarrollo: en producción llenan la consola y vuelven lenta la pestaña.
        if (process.env.NODE_ENV === 'development') JsSIP.debug.enable('JsSIP:*');
        else JsSIP.debug.disable();

        const socket = new JsSIP.WebSocketInterface(SIP_CONFIG.websocketUrl);
        const ua = new JsSIP.UA({
            sockets: [socket],
            uri: `sip:${ext}@${SIP_CONFIG.domain}`,
            password,
            authorization_user: username,
            register: true,
            register_expires: 300,
            // Espera entre reconexiones del WebSocket (JsSIP la duplica en cada intento)
            connection_recovery_min_interval: 5,
            connection_recovery_max_interval: 60,
        });

        // true en cuanto el WSS abrió alguna vez: si nunca abrió, el problema es
        // el certificado o un bloqueo, no un corte de red.
        let abrioAlgunaVez = false;

        ua.on('connecting', () => {
            if (uaRef.current !== ua || fallosRef.current > 0) return;
            updateStatus('connecting', 'Conectando...');
        });
        ua.on('connected', () => {
            if (uaRef.current !== ua) return;
            abrioAlgunaVez = true;
            updateStatus('connecting', 'Registrando SIP...');
        });
        // Se cayó el WebSocket. NO se suelta el UA: JsSIP reintenta solo, y soltarlo
        // dejaba un UA huérfano reintentando por detrás mientras se creaba otro encima.
        ua.on('disconnected', () => {
            if (uaRef.current !== ua) return;
            detenerKeepalive();
            setExtension(null);
            registrarFallo(abrioAlgunaVez ? MSG_ERROR_RED : MSG_ERROR_WSS);
        });
        ua.on('registered', () => {
            if (uaRef.current !== ua) return;
            fallosRef.current = 0;
            updateStatus('connected', `Conectado como ${ext}`);
            setExtension(ext);
            iniciarKeepalive(ua);
        });
        ua.on('registrationFailed', (e) => {
            if (uaRef.current !== ua) return;
            detenerKeepalive();
            setExtension(null);

            // Clave incorrecta o extensión rechazada: reintentar es justo lo que
            // fail2ban cuenta como ataque. Se detiene y se borran las credenciales.
            const { causes } = JsSIP.C;
            if (e.cause === causes.AUTHENTICATION_ERROR || e.cause === causes.REJECTED) {
                detenerUA();
                credsRef.current = null;
                sessionStorage.removeItem(SIP_SESSION_KEY);
                updateStatus('error', MSG_ERROR_CLAVE);
                showToast('error', MSG_ERROR_CLAVE);
                return;
            }

            // Cayó el WebSocket: lo cuenta 'disconnected' y JsSIP re-registra al reconectar.
            if (e.cause === causes.CONNECTION_ERROR) return;

            // Timeout u otro error con el WebSocket vivo: JsSIP no reintenta solo.
            if (registrarFallo(MSG_ERROR_RED)) return;
            const espera = Math.min(5000 * 2 ** (fallosRef.current - 1), 60000);
            reintentoRef.current = setTimeout(() => {
                if (uaRef.current === ua) ua.register();
            }, espera);
        });

        // newRTCSession dispara para TODA llamada (entrante y saliente).
        ua.on('newRTCSession', (data) => {
            const session = data.session;

            // Una llamada a la vez: una entrante durante otra se rechaza con ocupado.
            // Antes reemplazaba a la activa, y esa ya no se podía colgar.
            if (data.originator === 'remote' && (sessionRef.current || marcandoRef.current)) {
                session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
                return;
            }

            sessionRef.current = session;
            setupSessionListeners(session);

            // FIX del bug de JsSIP 3.x:
            // En llamadas salientes, 'peerconnection' ya se disparó antes de llegar aquí.
            // session.connection ya tiene el RTCPeerConnection creado, así que
            // adjuntamos los listeners directamente sin esperar el evento.
            if (session.connection) {
                setupPeerConnectionListeners(session.connection);
            }
        });

        uaRef.current = ua;
        ua.start();
    }

    /**
     * disconnect()
     * Cierra el registro SIP.
     */
    function disconnect() {
        generacionRef.current++;
        reclamoRef.current = null;
        sessionRef.current?.terminate();
        detenerUA();
        credsRef.current = null;
        sessionStorage.removeItem(SIP_SESSION_KEY);
        resetSipState();
    }

    /**
     * reconectar()
     * "Usar aquí" (pestaña en espera) o "Reintentar" (tras MAX_FALLOS).
     */
    function reconectar() {
        if (credsRef.current) connect(credsRef.current);
    }

    /**
     * openDialer({ trackingId, clientName, target })
     * Abre el marcador del PhonePanel precargado para un cliente.
     * trackingId debe ser el idCompra del cliente.
     */
    function openDialer(options = {}) {
        setDialerRequest({
            requestId: Date.now(),
            trackingId: options.trackingId ?? null,
            clientName: options.clientName ?? null,
            target: options.target ?? null,
        });
    }

    /**
     * makeCall(target, idCompra, callMeta)
     * Inicia una llamada al número indicado, asociada a una compra.
     *
     * @param {string}        target    Número a llamar (ej: "0991234567", "099 123 4567", "+593991234567")
     * @param {string|number} idCompra  OBLIGATORIO. Viaja en X-Tracking-ID y queda en el CDR
     *                                  (userfield). Sin idCompra válido no se marca.
     * @param {object}        callMeta  { clientName }
     * @returns {Promise<string|null>}  El idCompra enviado, o null si no se marcó.
     */
    async function makeCall(target, idCompra, callMeta = {}) {
        if (status === 'standby') {
            showToast('warning', 'El teléfono está activo en otra pestaña. Pulse "Usar aquí" en el teléfono para llamar desde esta.');
            return null;
        }
        if (!uaRef.current?.isRegistered()) {
            showToast('error', 'El teléfono no está registrado. Reconecta la extensión.');
            return null;
        }

        // Una llamada a la vez (también frena el doble clic mientras se pide el micrófono)
        if (marcandoRef.current || sessionRef.current) {
            showToast('warning', 'Ya hay una llamada en curso. Cuélgala antes de marcar otra.');
            return null;
        }

        if (!esIdCompraValido(idCompra)) {
            showToast('error', 'No se puede llamar: la llamada no está asociada a una compra (idCompra). '
                + 'Abre la gestión del cliente y marca desde allí.');
            return null;
        }
        const idCompraLlamada = String(idCompra).trim();

        const visibleTarget = String(target ?? '').trim();
        const destino = normalizarDestino(visibleTarget);
        if (!destino) {
            showToast('warning', 'No hay un número disponible para llamar.');
            return null;
        }

        marcandoRef.current = true;

        // Verificar micrófono antes de marcar para dar mensaje claro al usuario.
        // Se obtiene y se suelta inmediatamente — JsSIP lo pedirá de nuevo al hacer la llamada.
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            stream.getTracks().forEach(t => t.stop());
        } catch (err) {
            marcandoRef.current = false;
            const msgs = {
                NotAllowedError: 'Permiso de micrófono denegado. Habilítalo en la configuración del navegador.',
                NotFoundError: 'No se encontró ningún micrófono en este dispositivo.',
            };
            showToast('error', msgs[err.name] || 'No se pudo acceder al micrófono.');
            return null;
        }

        setCallTarget(visibleTarget);
        setCallClientName(callMeta.clientName || null);
        setTrackingId(idCompraLlamada);
        updateStatus('connecting', `Llamando a ${visibleTarget}...`);
        setCallState('ringing');

        try {
            const session = uaRef.current.call(`sip:${destino}@${SIP_CONFIG.domain}`, {
                mediaConstraints: { audio: true, video: false },
                // Asterisk copia esta cabecera al CDR (userfield): enlace llamada ↔ compra
                extraHeaders: [`X-Tracking-ID: ${idCompraLlamada}`],
                // Sin STUN: Asterisk está en la LAN. Con el STUN de Google, una PC sin
                // salida a internet esperaba su timeout antes de cada llamada.
                pcConfig: { iceServers: [] },
                rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
            });
            session.trackingId = idCompraLlamada;
            return idCompraLlamada;
        } catch (err) {
            showToast('error', 'Error al iniciar llamada: ' + err.message);
            endCallCleanUp();
            return null;
        }
    }

    /**
     * hangup()
     * Cuelga la llamada activa.
     */
    function hangup() {
        sessionRef.current?.terminate();
    }

    // ── Render ────────────────────────────────────────────────────────────
    // El elemento <audio> vive aquí (en el Provider) para que nunca se desmonte.
    // Si estuviera en PhoneWidget o en cualquier componente de ruta, se destruiría
    // al navegar y cortaría el audio a mitad de llamada.

    return (
        <SipContext.Provider value={{
            status, statusText, extension, phoneAvailable,
            callState, callSeconds, callTarget, callClientName, trackingId, dialerRequest,
            contextoGestion, setContextoGestion,
            connect, disconnect, makeCall, openDialer, hangup,
            reconectar, puedeReconectar: !!credsRef.current,
        }}>
            {children}
            <audio ref={audioRef} autoPlay />

            {/* ── Toast de notificaciones SIP ────────────────────────────── */}
            <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
                {notifications.map(n => (
                    <Toast
                        key={n.id}
                        id={n.id}
                        type={n.type}
                        message={n.message}
                        onDismiss={dismissToast}
                    />
                ))}
            </div>
        </SipContext.Provider>
    );
}

/**
 * useSip()
 * Hook para acceder al teléfono desde cualquier componente.
 *
 * Uso:
 *   const { makeCall, status } = useSip();
 *
 * Lanza error si se usa fuera de <SipProvider> (para detectar errores de integración).
 */
export function useSip() {
    const ctx = useContext(SipContext);
    if (!ctx) throw new Error('useSip debe usarse dentro de <SipProvider>');
    return ctx;
}
